// websocketStreamingService.js - VERSIÓN COMPLETA CON PERSISTENCIA MEJORADA Y ORDEN SECUENCIAL CORREGIDO
import { getAuth } from 'firebase/auth';

class WebSocketStreamingService {
  constructor() {
    this.ws = null;
    this.isConnecting = false;
    this.messageQueue = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    
    // ✅ KEEP-ALIVE MEJORADO
    this.keepAliveInterval = null;
    this.keepAliveIntervalMs = 30000; // 30 segundos
    this.lastActivity = Date.now();
    this.connectionCheckInterval = null;
    
    // ✅ MEJORA 1: Control de listeners activos
    this.currentHandler = null;
    this.isProcessingQuery = false;
    
    // ✅ MEJORA 2: Buffer mejorado con timeout
    this.tokenBuffer = new Map();
    this.tokenTimeouts = new Map();
    
    this.wsUrl = process.env.NODE_ENV === 'development'
      ? 'ws://localhost:3001'
      : 'wss://mz74rj6xuh.execute-api.us-east-2.amazonaws.com/dev';
    
    console.log('🔗 WebSocket URL configurada:', this.wsUrl);

    // ✅ NO AUTO-INICIAR - Solo iniciar cuando se necesite
    // this.startConnectionMonitoring();
  }

  // ✅ MONITOREO DE CONEXIÓN MEJORADO
  startConnectionMonitoring() {
    // Evitar múltiples intervalos
    if (this.connectionCheckInterval) {
      return;
    }

    // Verificar conexión cada minuto
    this.connectionCheckInterval = setInterval(() => {
      const timeSinceActivity = Date.now() - this.lastActivity;
      
      if (this.isConnected()) {
        // Si no hay actividad por más de 2 minutos, hacer ping
        if (timeSinceActivity > 120000) {
          console.log('📡 Enviando ping preventivo por inactividad');
          this.ping().catch(error => {
            console.warn('⚠️ Ping preventivo falló:', error.message);
          });
        }
      } else {
        // Si no está conectado pero debería estarlo, reconectar
        if (timeSinceActivity < 300000) { // 5 minutos
          console.log('🔄 Reconectando por pérdida de conexión...');
          this.connect().catch(error => {
            console.warn('⚠️ Reconexión automática falló:', error.message);
          });
        }
      }
    }, 60000); // Cada minuto
  }

  // ✅ KEEP-ALIVE AUTOMÁTICO
  startKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
    }

    this.keepAliveInterval = setInterval(() => {
      if (this.isConnected()) {
        console.log('💓 Enviando keep-alive ping...');
        this.ping().catch(error => {
          console.warn('⚠️ Keep-alive ping falló:', error.message);
        });
      }
    }, this.keepAliveIntervalMs);
  }

  // ✅ PARAR KEEP-ALIVE
  stopKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  // ✅ ACTUALIZAR ACTIVIDAD
  updateActivity() {
    this.lastActivity = Date.now();
  }

  // ✅ MEJORA 3: Limpieza segura de handlers
  cleanupCurrentHandler() {
    if (this.currentHandler && this.ws) {
      this.ws.removeEventListener('message', this.currentHandler);
      this.currentHandler = null;
    }
  }

  // Conectar al WebSocket MEJORADO
  async connect() {
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      console.log('🔗 WebSocket ya está conectado o conectando');
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        this.isConnecting = true;
        console.log('🔗 Conectando a WebSocket...', this.wsUrl);
        
        this.ws = new WebSocket(this.wsUrl);
        
        this.ws.onopen = () => {
          console.log('✅ WebSocket conectado exitosamente');
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.updateActivity();

          // ✅ INICIAR KEEP-ALIVE Y MONITOREO AL CONECTAR
          this.startKeepAlive();
          this.startConnectionMonitoring();

          this.processMessageQueue();
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            this.updateActivity(); // ✅ ACTUALIZAR ACTIVIDAD EN CADA MENSAJE
            
            const data = JSON.parse(event.data);
            console.log('📨 Mensaje base recibido:', data.type);
            
            // Manejar mensajes de sistema
            if (data.type === 'connected') {
              console.log('✅ Confirmación de conexión:', data.message);
            } else if (data.type === 'pong') {
              console.log('🏓 Pong recibido:', data.message);
            }
          } catch (error) {
            console.error('Error procesando mensaje base:', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('❌ WebSocket desconectado:', event.code, event.reason);
          this.isConnecting = false;
          
          // ✅ PARAR KEEP-ALIVE AL DESCONECTAR
          this.stopKeepAlive();
          
          // ✅ RECONEXIÓN AUTOMÁTICA MEJORADA
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts), 30000);
            setTimeout(() => {
              this.reconnectAttempts++;
              console.log(`🔄 Reintentando conexión WebSocket (${this.reconnectAttempts}/${this.maxReconnectAttempts}) en ${delay}ms`);
              this.connect().catch(error => {
                console.error('Error en reconexión:', error);
              });
            }, delay);
          }
        };

        this.ws.onerror = (error) => {
          console.error('❌ Error en WebSocket:', error);
          this.isConnecting = false;
          this.stopKeepAlive();
          reject(error);
        };

        // ✅ TIMEOUT MÁS LARGO PARA CONEXIÓN INICIAL
        setTimeout(() => {
          if (this.isConnecting) {
            console.error('⏰ Timeout conectando WebSocket');
            this.ws.close();
            reject(new Error('Timeout conectando WebSocket'));
          }
        }, 15000); // 15 segundos

      } catch (error) {
        console.error('❌ Error creando WebSocket:', error);
        this.isConnecting = false;
        this.stopKeepAlive();
        reject(error);
      }
    });
  }

  // Desconectar WebSocket
  disconnect() {
    if (this.ws) {
      console.log('🔌 Desconectando WebSocket...');
      this.ws.close(1000, 'Desconexión intencional');
      this.ws = null;
    }
    
    // ✅ LIMPIAR INTERVALOS
    this.stopKeepAlive();
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
      this.connectionCheckInterval = null;
    }
    
    // ✅ LIMPIAR HANDLERS Y BUFFERS
    this.cleanupCurrentHandler();
    this.tokenBuffer.clear();
    this.tokenTimeouts.clear();
    this.isProcessingQuery = false;
  }

  // Verificar si está conectado
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  // ✅ ENVIAR MENSAJE CON RECONEXIÓN AUTOMÁTICA
  async sendMessage(message) {
    // ✅ VERIFICAR Y RECONECTAR SI ES NECESARIO
    if (!this.isConnected()) {
      console.log('🔄 WebSocket no conectado, reconectando...');
      try {
        await this.connect();
      } catch (error) {
        console.error('Error reconectando para enviar mensaje:', error);
        throw error;
      }
    }

    if (this.isConnected()) {
      console.log('📤 Enviando mensaje via WebSocket:', message.action);
      this.ws.send(JSON.stringify(message));
      this.updateActivity();
    } else {
      console.log('📦 WebSocket no conectado, añadiendo mensaje a cola');
      this.messageQueue.push(message);
      
      if (!this.isConnecting) {
        try {
          await this.connect();
          if (this.isConnected()) {
            this.ws.send(JSON.stringify(message));
            this.updateActivity();
          }
        } catch (error) {
          console.error('Error conectando para enviar mensaje:', error);
          throw error;
        }
      }
    }
  }

  // Procesar mensajes en cola
  processMessageQueue() {
    console.log(`📦 Procesando ${this.messageQueue.length} mensajes en cola`);
    while (this.messageQueue.length > 0 && this.isConnected()) {
      const message = this.messageQueue.shift();
      console.log('📤 Enviando mensaje de cola:', message.action);
      this.ws.send(JSON.stringify(message));
      this.updateActivity();
    }
  }

  // ✅ CORRECCIÓN CRÍTICA: processTokenInOrder con debug extensivo
  processTokenInOrder(connectionId, tokenData, onToken) {
    if (!this.tokenBuffer.has(connectionId)) {
      this.tokenBuffer.set(connectionId, {
        tokens: [],
        expectedSequence: 1
      });
    }

    const buffer = this.tokenBuffer.get(connectionId);
    const { sequence, data } = tokenData;

    if (sequence !== undefined) {
      console.log(`📥 Token recibido #${sequence}: "${data?.substring(0, 30)}..."`);
      
      // ✅ VERIFICAR SI EL TOKEN ES VÁLIDO
      if (typeof data !== 'string') {
        console.warn(`⚠️ Token #${sequence} no es string:`, typeof data, data);
        return;
      }

      // ✅ VERIFICAR SI YA EXISTE ESTE TOKEN
      const existingToken = buffer.tokens.find(t => t.sequence === sequence);
      if (existingToken) {
        console.warn(`⚠️ Token duplicado #${sequence}, ignorando`);
        return;
      }
      
      // Agregar al buffer
      buffer.tokens.push({ sequence, data });
      buffer.tokens.sort((a, b) => a.sequence - b.sequence);
      
      // ✅ DEBUG: Estado del buffer
      console.log(`📊 Buffer estado - Esperando: ${buffer.expectedSequence}, En buffer: [${buffer.tokens.map(t => t.sequence).join(', ')}]`);
      
      // ✅ TIMEOUT POR TOKEN ESPECÍFICO
      const timeoutKey = `${connectionId}-${sequence}`;
      if (this.tokenTimeouts.has(timeoutKey)) {
        clearTimeout(this.tokenTimeouts.get(timeoutKey));
      }
      
      const timeout = setTimeout(() => {
        console.warn(`⏰ Timeout token #${sequence}, procesando buffer completo`);
        this.forceProcessBuffer(connectionId, onToken);
      }, 5000); // ✅ AUMENTAR A 5 SEGUNDOS
      
      this.tokenTimeouts.set(timeoutKey, timeout);
      
      // Procesar tokens en orden
      this.processBufferInOrder(connectionId, onToken);
      
    } else {
      // Token sin secuencia (fallback)
      console.log('📥 Token sin secuencia:', data?.substring(0, 30));
      
      // ✅ VERIFICAR QUE NO SEA UN NÚMERO DE SECUENCIA PERDIDO
      if (/^\d+$/.test(data?.trim())) {
        console.warn(`⚠️ Posible número de secuencia perdido: "${data}"`);
        return; // No procesar números sueltos
      }
      
      if (onToken && typeof data === 'string') {
        onToken(data);
      }
    }
  }

  // ✅ CORRECCIÓN CRÍTICA: Procesamiento ordenado con debug
  processBufferInOrder(connectionId, onToken) {
    const buffer = this.tokenBuffer.get(connectionId);
    if (!buffer) return;
    
    let processedCount = 0;
    
    while (buffer.tokens.length > 0 && buffer.tokens[0].sequence === buffer.expectedSequence) {
      const token = buffer.tokens.shift();
      
      // Limpiar timeout del token procesado
      const timeoutKey = `${connectionId}-${token.sequence}`;
      if (this.tokenTimeouts.has(timeoutKey)) {
        clearTimeout(this.tokenTimeouts.get(timeoutKey));
        this.tokenTimeouts.delete(timeoutKey);
      }
      
      console.log(`✅ Procesando token ordenado #${token.sequence}: "${token.data?.substring(0, 30)}..."`);
      
      // ✅ VERIFICACIÓN ADICIONAL ANTES DE PROCESAR
      if (typeof token.data === 'string' && token.data.length > 0) {
        if (onToken) {
          onToken(token.data);
        }
        processedCount++;
      } else {
        console.warn(`⚠️ Token #${token.sequence} inválido:`, token.data);
      }
      
      buffer.expectedSequence++;
    }
    
    if (processedCount > 0) {
      console.log(`✅ Procesados ${processedCount} tokens. Siguiente esperado: ${buffer.expectedSequence}`);
    }
    
    // ✅ DEBUG: Mostrar tokens pendientes
    if (buffer.tokens.length > 0) {
      const pendingSequences = buffer.tokens.map(t => t.sequence);
      console.log(`⏳ Tokens pendientes: [${pendingSequences.join(', ')}], esperando: ${buffer.expectedSequence}`);
      
      // ✅ VERIFICAR SI HAY UN GAP GRANDE
      const nextToken = buffer.tokens[0];
      if (nextToken && nextToken.sequence - buffer.expectedSequence > 3) {
        console.warn(`⚠️ Gap grande detectado (esperando ${buffer.expectedSequence}, siguiente es ${nextToken.sequence}). Forzando procesamiento...`);
        setTimeout(() => {
          this.forceProcessBuffer(connectionId, onToken);
        }, 2000);
      }
    }
  }

  // ✅ CORRECCIÓN: Forzar procesamiento mejorado
  forceProcessBuffer(connectionId, onToken) {
    const buffer = this.tokenBuffer.get(connectionId);
    if (!buffer || buffer.tokens.length === 0) return;
    
    console.log(`🔧 Forzando procesamiento de ${buffer.tokens.length} tokens pendientes`);
    
    // Ordenar y procesar todos los tokens restantes
    buffer.tokens.sort((a, b) => a.sequence - b.sequence);
    
    buffer.tokens.forEach(token => {
      console.log(`🔧 Token forzado #${token.sequence}: "${token.data?.substring(0, 30)}..."`);
      
      // ✅ VERIFICACIÓN ANTES DE PROCESAR FORZADO
      if (typeof token.data === 'string' && token.data.length > 0 && onToken) {
        onToken(token.data);
      }
      
      // Limpiar timeout
      const timeoutKey = `${connectionId}-${token.sequence}`;
      if (this.tokenTimeouts.has(timeoutKey)) {
        clearTimeout(this.tokenTimeouts.get(timeoutKey));
        this.tokenTimeouts.delete(timeoutKey);
      }
    });
    
    buffer.tokens = [];
    console.log(`🔧 Buffer limpio, tokens procesados forzadamente`);
  }

  // ✅ MEJORA 7: Limpieza completa de conexión
  clearConnectionBuffer(connectionId) {
    if (this.tokenBuffer.has(connectionId)) {
      console.log(`🧹 Limpiando buffer para conexión: ${connectionId}`);
      
      // Limpiar timeouts pendientes
      this.tokenTimeouts.forEach((timeout, key) => {
        if (key.startsWith(`${connectionId}-`)) {
          clearTimeout(timeout);
          this.tokenTimeouts.delete(key);
        }
      });
      
      this.tokenBuffer.delete(connectionId);
    }
  }

  // ✅ MEJORA 8: sendQueryStream conservador
  async sendQueryStream(query, conversationHistory = [], onToken, onComplete, onError) {
    // ✅ PREVENIR CONSULTAS SIMULTÁNEAS (conservador)
    if (this.isProcessingQuery) {
      console.log('⏸️ Esperando a que termine consulta anterior...');
      await new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!this.isProcessingQuery) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
      });
    }

    this.isProcessingQuery = true;
    const connectionId = Math.random().toString(36).substring(7);
    
    try {
      // ✅ ASEGURAR CONEXIÓN ANTES DE ENVIAR
      if (!this.isConnected()) {
        console.log('🔌 Conectando WebSocket para consulta...');
        await this.connect();
      }

      const auth = getAuth();
      const user = auth.currentUser;
      let token = '';
      
      if (user) {
        token = await user.getIdToken();
        console.log('🔐 Token Firebase obtenido para WebSocket');
      } else {
        console.log('⚠️ No hay usuario autenticado, enviando sin token');
      }

      console.log('🚀 Enviando consulta via WebSocket con persistencia mejorada');

      // ✅ LIMPIAR HANDLER ANTERIOR ANTES DE CREAR NUEVO
      this.cleanupCurrentHandler();

      // Configurar listeners para esta consulta específica
      const messageHandler = (event) => {
        try {
          this.updateActivity(); // ✅ ACTUALIZAR ACTIVIDAD
          
          const data = JSON.parse(event.data);
          console.log('📨 Mensaje WebSocket recibido:', data.type);

          switch (data.type) {
            case 'connected':
              console.log('✅ WebSocket conectado:', data.message);
              break;

            case 'start':
              console.log('▶️ Streaming iniciado:', data.message);
              // ✅ PREPARAR BUFFER LIMPIO
              this.tokenBuffer.set(connectionId, {
                tokens: [],
                expectedSequence: 1
              });
              break;

            case 'token':
              // ✅ USAR processTokenInOrder MEJORADO
              this.processTokenInOrder(connectionId, {
                sequence: data.sequence,
                data: data.data
              }, onToken);
              break;

            case 'complete':
              console.log('✅ Streaming completado');
              
              // ✅ PROCESAR TOKENS RESTANTES
              this.forceProcessBuffer(connectionId, onToken);
              
              // ✅ LIMPIEZA COMPLETA
              this.clearConnectionBuffer(connectionId);
              this.cleanupCurrentHandler();
              this.isProcessingQuery = false;
              
              if (onComplete) {
                onComplete({ 
                  success: true, 
                  actions: data.actions || [],
                  totalTokens: data.totalTokens
                });
              }
              break;

            case 'error':
              console.error('❌ Error en streaming:', data.message);
              this.clearConnectionBuffer(connectionId);
              this.cleanupCurrentHandler();
              this.isProcessingQuery = false;
              
              if (onError) {
                onError(new Error(data.message));
              }
              break;

            case 'pong':
              console.log('🏓 Pong recibido durante consulta');
              break;

            default:
              console.log('📨 Tipo de mensaje no reconocido:', data.type, data);
          }
        } catch (error) {
          console.error('Error procesando mensaje WebSocket:', error);
          this.isProcessingQuery = false;
          if (onError) {
            onError(error);
          }
        }
      };

      // ✅ ASIGNAR NUEVO HANDLER
      this.currentHandler = messageHandler;
      this.ws.addEventListener('message', messageHandler);

      // ✅ TIMEOUT MUCHO MÁS LARGO PARA CONSULTAS
      const timeoutId = setTimeout(() => {
        console.warn('⏰ Timeout en consulta WebSocket (10 minutos)');
        this.clearConnectionBuffer(connectionId);
        this.cleanupCurrentHandler();
        this.isProcessingQuery = false;
        if (onError) {
          onError(new Error('Timeout en la consulta - La consulta tomó demasiado tiempo'));
        }
      }, 600000); // ✅ 10 MINUTOS

      // Limpiar timeout cuando se complete
      const originalOnComplete = onComplete;
      const originalOnError = onError;
      
      const wrappedOnComplete = (result) => {
        clearTimeout(timeoutId);
        if (originalOnComplete) originalOnComplete(result);
      };
      
      const wrappedOnError = (error) => {
        clearTimeout(timeoutId);
        if (originalOnError) originalOnError(error);
      };

      // Redefinir callbacks con timeout
      onComplete = wrappedOnComplete;
      onError = wrappedOnError;

      // Enviar consulta
      await this.sendMessage({
        action: 'query',
        query,
        conversationHistory,
        token: token ? `Bearer ${token}` : undefined,
        connectionId: connectionId
      });

    } catch (error) {
      console.error('❌ Error en sendQueryStream WebSocket:', error);
      this.clearConnectionBuffer(connectionId);
      this.cleanupCurrentHandler();
      this.isProcessingQuery = false;
      if (onError) {
        onError(error);
      }
    }
  }

  // ✅ CORRECCIÓN CRÍTICA: sendImageQueryStream con debug adicional
  async sendImageQueryStream(query, imageBase64, conversationHistory = [], onToken, onComplete, onError) {
    console.log('🚀🖼️ INICIANDO sendImageQueryStream con debug...');
    
    // ✅ MISMA LÓGICA MEJORADA PERO PARA IMÁGENES
    if (this.isProcessingQuery) {
      console.log('⏸️ Esperando a que termine consulta anterior (imagen)...');
      await new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!this.isProcessingQuery) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
      });
    }

    this.isProcessingQuery = true;
    const connectionId = Math.random().toString(36).substring(7);
    
    try {
      if (!this.isConnected()) {
        console.log('🔌 Conectando WebSocket para imagen...');
        await this.connect();
      }

      const auth = getAuth();
      const user = auth.currentUser;
      let token = '';
      
      if (user) {
        token = await user.getIdToken();
        console.log('🔐 Token Firebase obtenido para imagen WebSocket');
      } else {
        console.log('⚠️ No hay usuario autenticado para imagen, enviando sin token');
      }

      console.log('🚀🖼️ Enviando consulta con imagen via WebSocket persistente');

      // ✅ LIMPIAR HANDLER ANTERIOR
      this.cleanupCurrentHandler();

      // ✅ MISMO HANDLER QUE TEXTO (funciona igual)
      const messageHandler = (event) => {
        try {
          this.updateActivity();
          const data = JSON.parse(event.data);
          console.log('📨🖼️ Mensaje imagen WebSocket recibido:', data.type, data.sequence ? `#${data.sequence}` : '');
          
          switch (data.type) {
            case 'connected':
              console.log('✅ WebSocket imagen conectado:', data.message);
              break;

            case 'start':
              console.log('▶️ Streaming imagen iniciado:', data.message);
              this.tokenBuffer.set(connectionId, {
                tokens: [],
                expectedSequence: 1
              });
              break;

            case 'token':
              // ✅ MISMA LÓGICA DE ORDENAMIENTO QUE TEXTO CON DEBUG ADICIONAL
              console.log(`📥🖼️ Token imagen #${data.sequence}: "${data.data?.substring(0, 20)}..."`);
              this.processTokenInOrder(connectionId, {
                sequence: data.sequence,
                data: data.data
              }, onToken);
              break;

            case 'complete':
              console.log('✅ Streaming imagen completado');
              this.forceProcessBuffer(connectionId, onToken);
              this.clearConnectionBuffer(connectionId);
              this.cleanupCurrentHandler();
              this.isProcessingQuery = false;
              
              if (onComplete) {
                onComplete({ 
                  success: true, 
                  actions: data.actions || [],
                  totalTokens: data.totalTokens
                });
              }
              break;

            case 'error':
              console.error('❌ Error en streaming imagen:', data.message);
              this.clearConnectionBuffer(connectionId);
              this.cleanupCurrentHandler();
              this.isProcessingQuery = false;
              
              if (onError) {
                onError(new Error(data.message));
              }
              break;

            case 'pong':
              console.log('🏓 Pong imagen recibido durante consulta');
              break;

            default:
              console.log('📨🖼️ Mensaje imagen no reconocido:', data.type);
          }
        } catch (error) {
          console.error('Error procesando mensaje imagen:', error);
          this.isProcessingQuery = false;
          if (onError) {
            onError(error);
          }
        }
      };

      this.currentHandler = messageHandler;
      this.ws.addEventListener('message', messageHandler);

      // ✅ TIMEOUT EXTENDIDO PARA IMÁGENES (15 minutos)
      const timeoutId = setTimeout(() => {
        console.warn('⏰ Timeout en consulta imagen WebSocket (15 minutos)');
        this.clearConnectionBuffer(connectionId);
        this.cleanupCurrentHandler();
        this.isProcessingQuery = false;
        if (onError) {
          onError(new Error('Timeout en la consulta de imagen - La consulta tomó demasiado tiempo'));
        }
      }, 900000); // 15 minutos para imágenes

      // Limpiar timeout cuando se complete
      const originalOnComplete = onComplete;
      const originalOnError = onError;
      
      const wrappedOnComplete = (result) => {
        clearTimeout(timeoutId);
        if (originalOnComplete) originalOnComplete(result);
      };
      
      const wrappedOnError = (error) => {
        clearTimeout(timeoutId);
        if (originalOnError) originalOnError(error);
      };

      // Redefinir callbacks con timeout
      onComplete = wrappedOnComplete;
      onError = wrappedOnError;

      // Enviar consulta imagen
      await this.sendMessage({
        action: 'imageQuery',
        query,
        imageBase64,
        conversationHistory,
        token: token ? `Bearer ${token}` : undefined,
        connectionId: connectionId
      });

    } catch (error) {
      console.error('❌ Error en sendImageQueryStream WebSocket:', error);
      this.clearConnectionBuffer(connectionId);
      this.cleanupCurrentHandler();
      this.isProcessingQuery = false;
      if (onError) {
        onError(error);
      }
    }
  }

  // ✅ PING MEJORADO
  async ping() {
    try {
      if (!this.isConnected()) {
        console.log('🔄 Reconectando para ping...');
        await this.connect();
      }

      console.log('🏓 Enviando ping...');
      await this.sendMessage({
        action: 'ping',
        timestamp: new Date().toISOString()
      });

      return true;
    } catch (error) {
      console.error('❌ Error enviando ping:', error);
      return false;
    }
  }

  // ✅ RECONEXIÓN MANUAL MEJORADA
  async reconnect() {
    console.log('🔄 Reconexión manual iniciada...');
    this.disconnect();
    this.reconnectAttempts = 0;
    
    try {
      await this.connect();
      console.log('✅ Reconexión manual exitosa');
      return true;
    } catch (error) {
      console.error('❌ Error en reconexión manual:', error);
      return false;
    }
  }

  // ✅ ESTADO MEJORADO
  getStatus() {
    return {
      connected: this.isConnected(),
      connecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts,
      queueSize: this.messageQueue.length,
      wsUrl: this.wsUrl,
      readyState: this.ws ? this.ws.readyState : null,
      bufferSize: this.tokenBuffer.size,
      lastActivity: new Date(this.lastActivity).toLocaleString(),
      timeSinceActivity: Date.now() - this.lastActivity,
      keepAliveActive: !!this.keepAliveInterval,
      isProcessingQuery: this.isProcessingQuery,
      hasCurrentHandler: !!this.currentHandler
    };
  }

  // ✅ DEBUG DE BUFFER
  getBufferStatus() {
    const bufferStatus = {};
    this.tokenBuffer.forEach((buffer, connectionId) => {
      bufferStatus[connectionId] = {
        tokensInBuffer: buffer.tokens.length,
        expectedSequence: buffer.expectedSequence,
        tokens: buffer.tokens.map(t => ({ seq: t.sequence, data: t.data?.substring(0, 10) + '...' }))
      };
    });
    return bufferStatus;
  }
}

// ✅ Exportar la clase directamente en lugar de una instancia
// Esto evita la inicialización automática del WebSocket
export default WebSocketStreamingService;